import React, { useState } from "react";
import {
  TextField,
  Button,
  Grid,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Chip,
  Box,
  IconButton,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Divider,
  InputAdornment,
  LinearProgress,
  Alert,
  AlertTitle,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { HelmetProvider } from "react-helmet-async";
import SEO from "../SEO";

const ApplyForm = ({ jobId, setSnackbar }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+91", // Default country code
    phone: "",
    resume: null,
    coverLetter: null, // Optional
    linkedinUrl: "",
    experienceRange: "",
    noticePeriod: "",
    currentCTC: "",
    expectedCTC: "",
    permanentAddress: "",
    city: "",
    state: "",
    country: "",
    workspace: "",
    referralSource: "",
    referralEmail: "", // Field for reference email
    openToRelocation: "Maybe", // Field for relocation preference
    previouslyEmployed: "No", // Field for previous employment
    qualifications: [
      {
        institution: "",
        degree: "",
        specialization: "",
        startYear: "",
        endYear: "",
      },
    ],
    skills: [], // Field for skills
    languages: [
      {
        language: "",
        proficiency: "",
      },
    ],
    workExperiences: [], // New field for work experiences
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const requiredFields = {
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone Number",
    resume: "Resume",
    permanentAddress: "Current Address",
    city: "City",
    state: "State/Province",
    country: "Country",
  };

  const COUNTRY_DATA = [
    { name: "Afghanistan", code: "AF", dialCode: "+93" },
    { name: "Albania", code: "AL", dialCode: "+355" },
    { name: "Algeria", code: "DZ", dialCode: "+213" },
    { name: "Andorra", code: "AD", dialCode: "+376" },
    { name: "Angola", code: "AO", dialCode: "+244" },
    { name: "Argentina", code: "AR", dialCode: "+54" },
    { name: "Armenia", code: "AM", dialCode: "+374" },
    { name: "Australia", code: "AU", dialCode: "+61" },
    { name: "Austria", code: "AT", dialCode: "+43" },
    { name: "Azerbaijan", code: "AZ", dialCode: "+994" },
    { name: "Bahamas", code: "BS", dialCode: "+1242" },
    { name: "Bahrain", code: "BH", dialCode: "+973" },
    { name: "Bangladesh", code: "BD", dialCode: "+880" },
    { name: "Belarus", code: "BY", dialCode: "+375" },
    { name: "Belgium", code: "BE", dialCode: "+32" },
    { name: "Belize", code: "BZ", dialCode: "+501" },
    { name: "Benin", code: "BJ", dialCode: "+229" },
    { name: "Bhutan", code: "BT", dialCode: "+975" },
    { name: "Bolivia", code: "BO", dialCode: "+591" },
    { name: "Bosnia and Herzegovina", code: "BA", dialCode: "+387" },
    { name: "Botswana", code: "BW", dialCode: "+267" },
    { name: "Brazil", code: "BR", dialCode: "+55" },
    { name: "Brunei", code: "BN", dialCode: "+673" },
    { name: "Bulgaria", code: "BG", dialCode: "+359" },
    { name: "Burkina Faso", code: "BF", dialCode: "+226" },
    { name: "Burundi", code: "BI", dialCode: "+257" },
    { name: "Cambodia", code: "KH", dialCode: "+855" },
    { name: "Cameroon", code: "CM", dialCode: "+237" },
    { name: "Canada", code: "CA", dialCode: "+1" },
    { name: "Cape Verde", code: "CV", dialCode: "+238" },
    { name: "Central African Republic", code: "CF", dialCode: "+236" },
    { name: "Chad", code: "TD", dialCode: "+235" },
    { name: "Chile", code: "CL", dialCode: "+56" },
    { name: "China", code: "CN", dialCode: "+86" },
    { name: "Colombia", code: "CO", dialCode: "+57" },
    { name: "Comoros", code: "KM", dialCode: "+269" },
    { name: "Congo", code: "CG", dialCode: "+242" },
    { name: "Costa Rica", code: "CR", dialCode: "+506" },
    { name: "Croatia", code: "HR", dialCode: "+385" },
    { name: "Cuba", code: "CU", dialCode: "+53" },
    { name: "Cyprus", code: "CY", dialCode: "+357" },
    { name: "Czech Republic", code: "CZ", dialCode: "+420" },
    { name: "Denmark", code: "DK", dialCode: "+45" },
    { name: "Djibouti", code: "DJ", dialCode: "+253" },
    { name: "Dominican Republic", code: "DO", dialCode: "+1809" },
    { name: "Ecuador", code: "EC", dialCode: "+593" },
    { name: "Egypt", code: "EG", dialCode: "+20" },
    { name: "El Salvador", code: "SV", dialCode: "+503" },
    { name: "Eritrea", code: "ER", dialCode: "+291" },
    { name: "Estonia", code: "EE", dialCode: "+372" },
    { name: "Ethiopia", code: "ET", dialCode: "+251" },
    { name: "Fiji", code: "FJ", dialCode: "+679" },
    { name: "Finland", code: "FI", dialCode: "+358" },
    { name: "France", code: "FR", dialCode: "+33" },
    { name: "Gabon", code: "GA", dialCode: "+241" },
    { name: "Gambia", code: "GM", dialCode: "+220" },
    { name: "Georgia", code: "GE", dialCode: "+995" },
    { name: "Germany", code: "DE", dialCode: "+49" },
    { name: "Ghana", code: "GH", dialCode: "+233" },
    { name: "Greece", code: "GR", dialCode: "+30" },
    { name: "Guatemala", code: "GT", dialCode: "+502" },
    { name: "Guinea", code: "GN", dialCode: "+224" },
    { name: "Haiti", code: "HT", dialCode: "+509" },
    { name: "Honduras", code: "HN", dialCode: "+504" },
    { name: "Hong Kong", code: "HK", dialCode: "+852" },
    { name: "Hungary", code: "HU", dialCode: "+36" },
    { name: "Iceland", code: "IS", dialCode: "+354" },
    { name: "India", code: "IN", dialCode: "+91" },
    { name: "Indonesia", code: "ID", dialCode: "+62" },
    { name: "Iran", code: "IR", dialCode: "+98" },
    { name: "Iraq", code: "IQ", dialCode: "+964" },
    { name: "Ireland", code: "IE", dialCode: "+353" },
    { name: "Israel", code: "IL", dialCode: "+972" },
    { name: "Italy", code: "IT", dialCode: "+39" },
    { name: "Jamaica", code: "JM", dialCode: "+1876" },
    { name: "Japan", code: "JP", dialCode: "+81" },
    { name: "Jordan", code: "JO", dialCode: "+962" },
    { name: "Kazakhstan", code: "KZ", dialCode: "+7" },
    { name: "Kenya", code: "KE", dialCode: "+254" },
    { name: "Kuwait", code: "KW", dialCode: "+965" },
    { name: "Kyrgyzstan", code: "KG", dialCode: "+996" },
    { name: "Laos", code: "LA", dialCode: "+856" },
    { name: "Latvia", code: "LV", dialCode: "+371" },
    { name: "Lebanon", code: "LB", dialCode: "+961" },
    { name: "Lesotho", code: "LS", dialCode: "+266" },
    { name: "Liberia", code: "LR", dialCode: "+231" },
    { name: "Libya", code: "LY", dialCode: "+218" },
    { name: "Liechtenstein", code: "LI", dialCode: "+423" },
    { name: "Lithuania", code: "LT", dialCode: "+370" },
    { name: "Luxembourg", code: "LU", dialCode: "+352" },
    { name: "Macau", code: "MO", dialCode: "+853" },
    { name: "Madagascar", code: "MG", dialCode: "+261" },
    { name: "Malawi", code: "MW", dialCode: "+265" },
    { name: "Malaysia", code: "MY", dialCode: "+60" },
    { name: "Maldives", code: "MV", dialCode: "+960" },
    { name: "Mali", code: "ML", dialCode: "+223" },
    { name: "Malta", code: "MT", dialCode: "+356" },
    { name: "Mauritania", code: "MR", dialCode: "+222" },
    { name: "Mauritius", code: "MU", dialCode: "+230" },
    { name: "Mexico", code: "MX", dialCode: "+52" },
    { name: "Moldova", code: "MD", dialCode: "+373" },
    { name: "Monaco", code: "MC", dialCode: "+377" },
    { name: "Mongolia", code: "MN", dialCode: "+976" },
    { name: "Montenegro", code: "ME", dialCode: "+382" },
    { name: "Morocco", code: "MA", dialCode: "+212" },
    { name: "Mozambique", code: "MZ", dialCode: "+258" },
    { name: "Myanmar", code: "MM", dialCode: "+95" },
    { name: "Namibia", code: "NA", dialCode: "+264" },
    { name: "Nepal", code: "NP", dialCode: "+977" },
    { name: "Netherlands", code: "NL", dialCode: "+31" },
    { name: "New Zealand", code: "NZ", dialCode: "+64" },
    { name: "Nicaragua", code: "NI", dialCode: "+505" },
    { name: "Niger", code: "NE", dialCode: "+227" },
    { name: "Nigeria", code: "NG", dialCode: "+234" },
    { name: "North Korea", code: "KP", dialCode: "+850" },
    { name: "North Macedonia", code: "MK", dialCode: "+389" },
    { name: "Norway", code: "NO", dialCode: "+47" },
    { name: "Oman", code: "OM", dialCode: "+968" },
    { name: "Pakistan", code: "PK", dialCode: "+92" },
    { name: "Palestine", code: "PS", dialCode: "+970" },
    { name: "Panama", code: "PA", dialCode: "+507" },
    { name: "Papua New Guinea", code: "PG", dialCode: "+675" },
    { name: "Paraguay", code: "PY", dialCode: "+595" },
    { name: "Peru", code: "PE", dialCode: "+51" },
    { name: "Philippines", code: "PH", dialCode: "+63" },
    { name: "Poland", code: "PL", dialCode: "+48" },
    { name: "Portugal", code: "PT", dialCode: "+351" },
    { name: "Qatar", code: "QA", dialCode: "+974" },
    { name: "Romania", code: "RO", dialCode: "+40" },
    { name: "Russia", code: "RU", dialCode: "+7" },
    { name: "Rwanda", code: "RW", dialCode: "+250" },
    { name: "Saudi Arabia", code: "SA", dialCode: "+966" },
    { name: "Senegal", code: "SN", dialCode: "+221" },
    { name: "Serbia", code: "RS", dialCode: "+381" },
    { name: "Sierra Leone", code: "SL", dialCode: "+232" },
    { name: "Singapore", code: "SG", dialCode: "+65" },
    { name: "Slovakia", code: "SK", dialCode: "+421" },
    { name: "Slovenia", code: "SI", dialCode: "+386" },
    { name: "Somalia", code: "SO", dialCode: "+252" },
    { name: "South Africa", code: "ZA", dialCode: "+27" },
    { name: "South Korea", code: "KR", dialCode: "+82" },
    { name: "South Sudan", code: "SS", dialCode: "+211" },
    { name: "Spain", code: "ES", dialCode: "+34" },
    { name: "Sri Lanka", code: "LK", dialCode: "+94" },
    { name: "Sudan", code: "SD", dialCode: "+249" },
    { name: "Sweden", code: "SE", dialCode: "+46" },
    { name: "Switzerland", code: "CH", dialCode: "+41" },
    { name: "Syria", code: "SY", dialCode: "+963" },
    { name: "Taiwan", code: "TW", dialCode: "+886" },
    { name: "Tajikistan", code: "TJ", dialCode: "+992" },
    { name: "Tanzania", code: "TZ", dialCode: "+255" },
    { name: "Thailand", code: "TH", dialCode: "+66" },
    { name: "Timor-Leste", code: "TL", dialCode: "+670" },
    { name: "Togo", code: "TG", dialCode: "+228" },
    { name: "Trinidad and Tobago", code: "TT", dialCode: "+1868" },
    { name: "Tunisia", code: "TN", dialCode: "+216" },
    { name: "Turkey", code: "TR", dialCode: "+90" },
    { name: "Turkmenistan", code: "TM", dialCode: "+993" },
    { name: "Uganda", code: "UG", dialCode: "+256" },
    { name: "Ukraine", code: "UA", dialCode: "+380" },
    { name: "United Arab Emirates", code: "AE", dialCode: "+971" },
    { name: "United Kingdom", code: "GB", dialCode: "+44" },
    { name: "United States", code: "US", dialCode: "+1" },
    { name: "Uruguay", code: "UY", dialCode: "+598" },
    { name: "Uzbekistan", code: "UZ", dialCode: "+998" },
    { name: "Venezuela", code: "VE", dialCode: "+58" },
    { name: "Vietnam", code: "VN", dialCode: "+84" },
    { name: "Yemen", code: "YE", dialCode: "+967" },
    { name: "Zambia", code: "ZM", dialCode: "+260" },
    { name: "Zimbabwe", code: "ZW", dialCode: "+263" },
  ];

  const getFlag = (code) => {
    return code
      .toUpperCase()
      .split("")
      .map((c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
      .join("");
  };

  const countryCodes = COUNTRY_DATA.map((c) => ({
    code: c.dialCode,
    name: c.name,
    flag: getFlag(c.code),
  }));

  const countries = COUNTRY_DATA.map((c) => ({ name: c.name, code: c.code }));
  const calculateProgress = () => {
    const requiredFields = Object.keys(requiredFields);
    const filledFields = requiredFields.filter((field) => !!formData[field]);
    return (filledFields.length / requiredFields.length) * 100;
  };
  // Update the handleChange function
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // Clear error for this field
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (name === "resume" || name === "coverLetter") {
      setFormData({ ...formData, [name]: files ? files[0] : null });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Handler for qualification fields
  const handleQualificationChange = (index, field, value) => {
    const updatedQualifications = [...formData.qualifications];
    updatedQualifications[index] = {
      ...updatedQualifications[index],
      [field]: value,
    };
    setFormData({ ...formData, qualifications: updatedQualifications });
  };

  // Add new qualification
  const addQualification = () => {
    setFormData({
      ...formData,
      qualifications: [
        ...formData.qualifications,
        {
          institution: "",
          degree: "",
          specialization: "",
          startYear: "",
          endYear: "",
        },
      ],
    });
  };

  // Remove qualification
  const removeQualification = (index) => {
    const updatedQualifications = [...formData.qualifications];
    updatedQualifications.splice(index, 1);
    setFormData({ ...formData, qualifications: updatedQualifications });
  };

  // Handler for language fields
  const handleLanguageChange = (index, field, value) => {
    const updatedLanguages = [...formData.languages];
    updatedLanguages[index] = {
      ...updatedLanguages[index],
      [field]: value,
    };
    setFormData({ ...formData, languages: updatedLanguages });
  };

  // Add new language
  const addLanguage = () => {
    setFormData({
      ...formData,
      languages: [
        ...formData.languages,
        {
          language: "",
          proficiency: "",
        },
      ],
    });
  };

  // Remove language
  const removeLanguage = (index) => {
    const updatedLanguages = [...formData.languages];
    updatedLanguages.splice(index, 1);
    setFormData({ ...formData, languages: updatedLanguages });
  };

  // Work Experience Handlers
  const addWorkExperience = () => {
    setFormData({
      ...formData,
      workExperiences: [
        ...formData.workExperiences,
        {
          jobTitle: "",
          organization: "",
          startDate: "",
          endDate: "",
          currentlyWorking: false,
          jobDescription: "",
          location: "",
        },
      ],
    });
  };

  const removeWorkExperience = (index) => {
    const updatedWorkExperiences = [...formData.workExperiences];
    updatedWorkExperiences.splice(index, 1);
    setFormData({ ...formData, workExperiences: updatedWorkExperiences });
  };

  const handleWorkExperienceChange = (index, field, value) => {
    const updatedWorkExperiences = [...formData.workExperiences];

    // Special handling for the checkbox
    if (field === "currentlyWorking") {
      updatedWorkExperiences[index] = {
        ...updatedWorkExperiences[index],
        [field]: value,
        // Clear end date if currently working
        ...(value && { endDate: "" }),
      };
    } else {
      updatedWorkExperiences[index] = {
        ...updatedWorkExperiences[index],
        [field]: value,
      };
    }

    setFormData({ ...formData, workExperiences: updatedWorkExperiences });
  };

  // Handle skills input
  const handleSkillInputChange = (e) => {
    setSkillInput(e.target.value);
  };

  // Add a skill when Enter is pressed
  const handleSkillKeyDown = (e) => {
    if (e.key === "Enter" && skillInput.trim() !== "") {
      e.preventDefault();
      addSkill();
    }
  };

  // Add a skill
  const addSkill = () => {
    if (skillInput.trim() !== "" && formData.skills.length < 50) {
      setFormData({
        ...formData,
        skills: [...formData.skills, skillInput.trim()],
      });
      setSkillInput("");
    }
  };

  // Remove a skill
  const removeSkill = (skillToRemove) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter((skill) => skill !== skillToRemove),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      firstName,
      lastName,
      email,
      countryCode,
      phone,
      resume,
      qualifications,
      skills,
      languages,
      openToRelocation,
      previouslyEmployed,
    } = formData;

    // Validate required fields
    if (!firstName || !lastName || !email || !phone || !resume) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please fill in all required fields.",
      });
      return;
    }

    // Validate at least one qualification is entered
    if (
      formData.qualifications.length === 0 ||
      !formData.qualifications[0].institution
    ) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please add at least one qualification.",
      });
      return;
    }

    // Check for validation errors
    let errors = {};
    let hasErrors = false;

    // Check each required field
    Object.keys(requiredFields).forEach((field) => {
      if (!formData[field]) {
        errors[field] = `${requiredFields[field]} is required`;
        hasErrors = true;
      }
    });

    // Additional validation for qualifications
    if (
      formData.qualifications.length === 0 ||
      !formData.qualifications[0].institution
    ) {
      errors.qualifications = "At least one qualification is required";
      hasErrors = true;
    }

    if (hasErrors) {
      setFieldErrors(errors);
      // Scroll to the first error
      const firstErrorField = document.querySelector(".Mui-error");
      if (firstErrorField)
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });

      setSnackbar({
        open: true,
        severity: "error",
        message: "Please fill in all required fields.",
      });
      return;
    }

    setSubmitting(true);

    const formDataToSend = new FormData();
    formDataToSend.append("jobId", jobId);
    formDataToSend.append("firstName", firstName);
    formDataToSend.append("lastName", lastName);
    formDataToSend.append("email", email);
    formDataToSend.append("countryCode", countryCode);
    formDataToSend.append("phone", phone);
    formDataToSend.append("resume", resume);

    // Append optional fields if they exist
    if (formData.coverLetter) {
      formDataToSend.append("coverLetter", formData.coverLetter);
    }

    formDataToSend.append("linkedinUrl", formData.linkedinUrl || "");
    formDataToSend.append("experienceRange", formData.experienceRange || "");
    formDataToSend.append("noticePeriod", formData.noticePeriod || "");
    formDataToSend.append("currentCTC", formData.currentCTC || "");
    formDataToSend.append("expectedCTC", formData.expectedCTC || "");
    formDataToSend.append("permanentAddress", formData.permanentAddress || "");
    formDataToSend.append("city", formData.city || "");
    formDataToSend.append("state", formData.state || "");
    formDataToSend.append("country", formData.country || "");
    formDataToSend.append("workspace", formData.workspace || "");
    formDataToSend.append("referralSource", formData.referralSource || "");
    formDataToSend.append("referralEmail", formData.referralEmail || "");
    formDataToSend.append("openToRelocation", openToRelocation || "");
    formDataToSend.append("previouslyEmployed", previouslyEmployed || "");

    // Append qualifications as JSON
    formDataToSend.append(
      "qualifications",
      JSON.stringify(formData.qualifications)
    );

    // Append skills as JSON
    formDataToSend.append("skills", JSON.stringify(formData.skills));

    // Append languages as JSON
    formDataToSend.append("languages", JSON.stringify(formData.languages));

    // Append work experiences as JSON
    formDataToSend.append(
      "workExperiences",
      JSON.stringify(formData.workExperiences)
    );

    setSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/applications/submit`,
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      const result = await response.json();

      setSnackbar({
        open: true,
        severity: response.ok ? "success" : "error",
        message: response.ok
          ? "Application submitted successfully!"
          : result.message || "Failed to submit application.",
      });

      if (response.ok) {
        // Reset form on successful submission
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          countryCode: "+91",
          phone: "",
          resume: null,
          coverLetter: null,
          linkedinUrl: "",
          experienceRange: "",
          noticePeriod: "",
          currentCTC: "",
          expectedCTC: "",
          permanentAddress: "",
          city: "",
          state: "",
          country: "",
          workspace: "",
          referralSource: "",
          referralEmail: "",
          openToRelocation: "Maybe",
          previouslyEmployed: "No",
          qualifications: [
            {
              institution: "",
              degree: "",
              specialization: "",
              startYear: "",
              endYear: "",
            },
          ],
          skills: [],
          languages: [
            {
              language: "",
              proficiency: "",
            },
          ],
          workExperiences: [],
        });
        setSkillInput("");
      }
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "An error occurred while submitting your application.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        {Object.keys(fieldErrors).length > 0 && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Please correct the following errors:</AlertTitle>
              <ul>
                {Object.values(fieldErrors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          </Grid>
        )}
        <LinearProgress
          variant="determinate"
          value={
            (Object.keys(requiredFields).filter((field) => !!formData[field])
              .length /
              Object.keys(requiredFields).length) *
            100
          }
          sx={{ mb: 3 }}
          aria-label="Form completion progress"
        />
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            className="formInputField"
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            error={!!fieldErrors.firstName}
            helperText={fieldErrors.firstName || ""}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            className="formInputField"
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            error={!!fieldErrors.lastName}
            helperText={fieldErrors.lastName || ""}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Contact information */}
        <Grid item xs={12}>
          <TextField
            className="formInputField"
            fullWidth
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Country Code</InputLabel>
            <Select
            className="formInputField"
              value={formData.countryCode}
              name="countryCode"
              onChange={handleChange}
              label="Country Code"
              required
            >
              {countryCodes.map((country) => (
                <MenuItem key={`${country.name}-${country.code}`} value={country.code}>
                  <span style={{ marginRight: "8px" }}>{country.flag}</span>
                  {country.name} ({country.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            className="formInputField"
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </Grid>

        {/* Resume Upload */}
        {/* Resume Upload */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!fieldErrors.resume}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              color={formData.resume ? "success" : "secondary"}
              startIcon={<UploadFileIcon />}
              sx={{padding:"10px 0px"}}
            >
              {formData.resume ? "Resume Uploaded" : "Upload Resume *"}
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx"
                name="resume"
                onChange={handleChange}
                required
              />
            </Button>
            {formData.resume && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {formData.resume.name}
              </Typography>
            )}
            {fieldErrors.resume && (
              <FormHelperText error>{fieldErrors.resume}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Cover Letter Upload */}
        <Grid item xs={12} sm={6}>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            color={formData.coverLetter ? "success" : "primary"}
            startIcon={<UploadFileIcon />}
            sx={{padding:"10px 0px"}}
          >
            {formData.coverLetter
              ? "Cover Letter Uploaded"
              : "Upload Cover Letter"}
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx"
              name="coverLetter"
              onChange={handleChange}
            />
          </Button>
          {formData.coverLetter && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {formData.coverLetter.name}
            </Typography>
          )}
        </Grid>

        {/* Qualifications Section */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{fontSize:"16px", marginTop:"8px"}} gutterBottom>
            Qualifications
          </Typography>
          {formData.qualifications.map((qualification, index) => (
            <Grid
              key={index}
              elevation={1}
              sx={{ mb: 2, position: "relative" }}
            >
              <Grid container spacing={2} sx={index > 0 ? { backgroundColor: '#f7f7f7', borderRadius:'8px', border:'solid 1px #ccc', p: 2, marginLeft:'0px', paddingLeft:'0px', paddingTop:'0px', marginTop:'10px', width:'calc(100% - 0px)' } : {}}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    className="formInputField"
                    label="School/University Name"
                    value={qualification.institution}
                    onChange={(e) =>
                      handleQualificationChange(
                        index,
                        "institution",
                        e.target.value
                      )
                    }
                    required
                    sx={index > 0 ? {width:'calc(100% - 36px)', backgroundColor:'#fff'} : {backgroundColor:'#fff'}}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{backgroundColor:"#fff"}}
                    className="formInputField"
                    label="Degree"
                    value={qualification.degree}
                    onChange={(e) =>
                      handleQualificationChange(index, "degree", e.target.value)
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{backgroundColor:"#fff"}}
                    className="formInputField"
                    label="Specialization"
                    value={qualification.specialization}
                    onChange={(e) =>
                      handleQualificationChange(
                        index,
                        "specialization",
                        e.target.value
                      )
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{backgroundColor:"#fff"}}
                    className="formInputField"
                    label="Start Year"
                    type="number"
                    value={qualification.startYear}
                    onChange={(e) =>
                      handleQualificationChange(
                        index,
                        "startYear",
                        e.target.value
                      )
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    sx={{backgroundColor:"#fff"}}
                    className="formInputField"
                    label="End Year"
                    type="number"
                    value={qualification.endYear}
                    onChange={(e) =>
                      handleQualificationChange(
                        index,
                        "endYear",
                        e.target.value
                      )
                    }
                    required
                  />
                </Grid>
                {index > 0 && (
                  <IconButton
                    aria-label="delete"
                    onClick={() => removeQualification(index)}
                    sx={{ position: "absolute", top:20, right: 10, color:'red', }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addQualification}
            sx={{ padding:"10px 16px" }}
          >
            Add Another Qualification
          </Button>
        </Grid>

        {/* Work Experience Section */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{fontSize:"16px", marginTop:"8px"}} gutterBottom>
            Work Experience
          </Typography>

          {formData.workExperiences.length > 0 ? (
            formData.workExperiences.map((experience, index) => (
              <Paper
                key={index}
                elevation={1}
                sx={{ p: 2, mb: 2, position: "relative" }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      className="formInputField"
                      label="Job Title"
                      value={experience.jobTitle}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          index,
                          "jobTitle",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      sx={{width:'calc(100% - 36px)'}}
                      className="formInputField"
                      label="Organization"
                      value={experience.organization}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          index,
                          "organization",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      className="formInputField"
                      label="Start Date (MM/YY)"
                      placeholder="MM/YY"
                      value={experience.startDate}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          index,
                          "startDate",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          className="formInputField"
                          label="End Date (MM/YY)"
                          placeholder="MM/YY"
                          value={experience.endDate}
                          onChange={(e) =>
                            handleWorkExperienceChange(
                              index,
                              "endDate",
                              e.target.value
                            )
                          }
                          disabled={experience.currentlyWorking}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={experience.currentlyWorking}
                              onChange={(e) =>
                                handleWorkExperienceChange(
                                  index,
                                  "currentlyWorking",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Current"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Job Description"
                      multiline
                      rows={3}
                      value={experience.jobDescription}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          index,
                          "jobDescription",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      className="formInputField"
                      label="Location"
                      value={experience.location}
                      onChange={(e) =>
                        handleWorkExperienceChange(
                          index,
                          "location",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <IconButton
                    aria-label="delete"
                    onClick={() => removeWorkExperience(index)}
                    sx={{ position: "absolute", top:20, right: 10, color:'red' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Paper>
            ))
          ) : (
            <Typography color="textSecondary" sx={{ mb: 2 }}>
              No work experience added. (Optional)
            </Typography>
          )}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addWorkExperience}
            sx={{padding:"10px 16px" }}
          >
            Add Work Experience
          </Button>
        </Grid>

        {/* Skills Section */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{fontSize:"16px", marginTop:"8px"}} gutterBottom>
            Skills
          </Typography>
          <Box sx={{ display: "flex", mb: 2 }}>
            <TextField
              fullWidth
              className="formInputField"
              label="Add your skills"
              value={skillInput}
              onChange={handleSkillInputChange}
              onKeyDown={handleSkillKeyDown}
              sx={{ mr: 1 }}
            />
            <Button variant="contained" onClick={addSkill}>
              Add
            </Button>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {formData.skills.map((skill, index) => (
              <Chip
                key={index}
                label={skill}
                onDelete={() => removeSkill(skill)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Grid>

        {/* Languages Section */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{fontSize:"16px"}} gutterBottom>
            Languages
          </Typography>
          {formData.languages.map((language, index) => (
            <Grid
              key={index}
              elevation={1}
              sx={{ mb: 2, position: "relative" }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    className="formInputField"
                    label="Language"
                    value={language.language}
                    onChange={(e) =>
                      handleLanguageChange(index, "language", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Proficiency</InputLabel>
                    <Select
                    className="formInputField"
                    sx={ index > 0 ? {width:'calc(100% - 40px)'} : {} }
                      value={language.proficiency}
                      onChange={(e) =>
                        handleLanguageChange(
                          index,
                          "proficiency",
                          e.target.value
                        )
                      }
                      label="Proficiency"
                    >
                      <MenuItem value="Beginner">Beginner</MenuItem>
                      <MenuItem value="Classroom Study">
                        Classroom Study
                      </MenuItem>
                      <MenuItem value="Fluent">Fluent</MenuItem>
                      <MenuItem value="Advanced">Advanced</MenuItem>
                      <MenuItem value="Native">Native</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {index > 0 && (
                  <IconButton
                    aria-label="delete"
                    onClick={() => removeLanguage(index)}
                    sx={{ position: "absolute", top: 20, right:0, color:'red' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addLanguage}
            sx={{ mt: 1, padding:"10px 16px"}}
          >
            Add Another Language
          </Button>
        </Grid>

        {/* Additional Personal Information */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{fontSize:"16px", lineHeight:"16px", marginTop:"8px", marginBottom:"0px"}} gutterBottom>
            Additional Information
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Experience Range</InputLabel>
            <Select
              name="experienceRange"
              value={formData.experienceRange}
              onChange={handleChange}
              label="Experience Range"
              className="formInputField"
            >
              <MenuItem value="0-1 years">0-1 years</MenuItem>
              <MenuItem value="1-3 years">1-3 years</MenuItem>
              <MenuItem value="3-5 years">3-5 years</MenuItem>
              <MenuItem value="5-7 years">5-7 years</MenuItem>
              <MenuItem value="7-10 years">7-10 years</MenuItem>
              <MenuItem value="10+ years">10+ years</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Notice Period *</InputLabel>
            <Select
              name="noticePeriod"
              value={formData.noticePeriod}
              required
              onChange={handleChange}
              label="Notice Period"
              className="formInputField"
            >
              <MenuItem value="Immediate">Immediate</MenuItem>
              <MenuItem value="15 days">15 days</MenuItem>
              <MenuItem value="30 days">30 days</MenuItem>
              <MenuItem value="60 days">60 days</MenuItem>
              <MenuItem value="90 days">90 days</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            className="formInputField"
            label="Current CTC"
            name="currentCTC"
            value={formData.currentCTC}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            className="formInputField"
            label="Expected CTC"
            name="expectedCTC"
            value={formData.expectedCTC}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Workspace Preference</InputLabel>
            <Select
              name="workspace"
              value={formData.workspace}
              onChange={handleChange}
              label="Workspace Preference"
              className="formInputField"
            >
              <MenuItem value="Remote">Remote</MenuItem>
              <MenuItem value="Hybrid">Hybrid</MenuItem>
              <MenuItem value="On-site">On-site</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Open to Relocation</InputLabel>
            <Select
              name="openToRelocation"
              value={formData.openToRelocation}
              onChange={handleChange}
              label="Open to Relocation"
              className="formInputField"
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
              <MenuItem value="Maybe">Maybe</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Previously Employed</InputLabel>
            <Select
              name="previouslyEmployed"
              value={formData.previouslyEmployed}
              onChange={handleChange}
              label="Previously Employed"
              className="formInputField"
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Referral Source</InputLabel>
            <Select
              name="referralSource"
              value={formData.referralSource}
              onChange={handleChange}
              label="Referral Source"
              className="formInputField"
            >
              <MenuItem value="LinkedIn">LinkedIn</MenuItem>
              <MenuItem value="Job Portal">Job Portal</MenuItem>
              <MenuItem value="Company Website">Company Website</MenuItem>
              <MenuItem value="Employee Referral">Employee Referral</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {formData.referralSource === "Employee Referral" && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Referral Email"
              name="referralEmail"
              value={formData.referralEmail}
              onChange={handleChange}
            />
          </Grid>
        )}

        {/* Address Information */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{fontSize:"16px", lineHeight:"16px", marginTop:"8px", marginBottom:"0px"}} gutterBottom>
            Address Information
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            className="formInputField"
            label="Current Address"
            name="permanentAddress"
            value={formData.permanentAddress}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            className="formInputField"
            label="City"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            className="formInputField"
            label="State/Province"
            name="state"
            value={formData.state}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth required>
            <InputLabel>Country</InputLabel>
            <Select
              name="country"
              value={formData.country}
              onChange={handleChange}
              label="Country"
              className="formInputField"
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.name}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* LinkedIn URL */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            className="formInputField"
            label="LinkedIn Profile URL"
            name="linkedinUrl"
            value={formData.linkedinUrl}
            onChange={handleChange}
          />
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
          sx={{ padding:"14px 16px", width:"100%" }}
            type="submit"
            variant="contained"
            color="primary"
            disabled={submitting}
            startIcon={submitting && <CircularProgress size={20} />}
          >
            {submitting ? "Submitting..." : "Submit Application"}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ApplyForm;
